import { useApiClient } from "./useApiClient";
import { useCallback } from "react";
import type {
  Item,
  BiddingInfoItem,
  BiddingInfoSubItem,
  BiddingInfoData,
  UpdateBiddingDataRequest,
  BulkUpdateStatusRequest,
  BiddingItemStatusUpdateRequest,
  BiddingItemStatusUpdateResult,
  BulkResult,
  UpcomingBiddingListResponse,
  BiddingInfoResultResponse,
  BiddingSchedulesRequest,
  BiddingSchedulesResponse,
  SystemMaster,
  ManualNumberingResponse,
  BiddingItemResponse,
  ExcelInfoItem
} from "../types";
import { type ItemStatusType } from "../constants";
import type { FavoriteKeys } from "../types/FavoriteKey";
import type { BiddingConfig } from "../types/BiddingConfig";
import type { BiddingInfoResponse } from "../types/BiddingInfoResponse";

// API関連のロジックだけをまとめたカスタムフック
export const useItemsApi = () => {
  const api = useApiClient();

  // 指定された条件に一致する出品アイテム一覧を取得します。
  const biddingItemsGetItemList = useCallback(
    async (queryString: string): Promise<BiddingItemResponse> => {
      const response = await api.get(`/BiddingItems?${queryString}`);
      return response.data;
    },
    [api],
  );

  // 指定された条件に一致する単一の出品アイテムを取得します。
  const biddingItemsGetItem = useCallback(
    async (
      itemId: string,
      branchNumber: number,
    ): Promise<BiddingInfoItem | null> => {
      const response = await api.get(`/BiddingItems/${itemId}/${branchNumber}`);
      return response.data;
    },
    [api],
  );

  // 出品アイテム（出品メインアイテム＋出品サブアイテム）を１件新規登録します。
  const createBiddingItem = useCallback(
    async (itemToCreate: BiddingInfoItem): Promise<BiddingInfoItem | null> => {
      // API送信用に、リクエストデータを変換する
      const payload = prepareItemForApi(itemToCreate);
      return await api.post(`/BiddingItems`, payload);
    },
    [api],
  );

  // 出品メインアイテムを１件更新します。
  const updateBiddingItem = useCallback(
    async (itemToUpdate: BiddingInfoItem): Promise<boolean> => {
      // API送信用に、リクエストデータを変換する
      const updatedData = prepareItemForApi(itemToUpdate);
      await api.put<BiddingInfoItem>(
        `/BiddingItems/${itemToUpdate.itemId}`,
        updatedData,
      ); // API エンドポイントと戻り値の型は適宜調整
      return true;
    },
    [api],
  );

  /**
   * 出品アイテムの一括登録をします。
   */
  const bulkCreateBiddingItem = useCallback(
    async (itemToCreate: ExcelInfoItem[], prefectureType: number): Promise<BulkResult | null> => {
      // API送信用に、リクエストデータを変換する
      const payload = prepareExcelItemListForApi(itemToCreate);
      const response = await api.post(`/BiddingItems/BulkCreateBiddingItems?prefectureType=${prefectureType}`, payload);
      return response.data;
  }, [api]);

  /**
   * API送信用に、空文字列をnullに変換するヘルパー関数
   * C#の int型に対応するため
   */
  const prepareItemForApi = (newItemData: BiddingInfoItem) => {
    const { itemId, receptionDate, ...itemDataForApi } = newItemData;

    // 空文字やundefinedの場合は null を返し、それ以外は数値に変換する関数
    const toNumberOrNull = (val: any) => {
      if (val === '' || val === null || val === undefined) return null;
      return Number(val);
    };

    const payload = {
      ...itemDataForApi, // まず全てのプロパティをコピー

      biddingDate: Number(newItemData.biddingDate) || 0,

      // '' (空文字) を 0 に変換する (C#のint型に合わせる)
      capacity1: Number(newItemData.capacity1) || 0,
      quantity1: Number(newItemData.quantity1) || 0,
      capacity2: Number(newItemData.capacity2) || 0,
      quantity2: Number(newItemData.quantity2) || 0,
      bidMinPrice: Number(newItemData.bidMinPrice) || 0,

      // 他の '' を許容している数値フィールドも同様に変換
      sellerCode: Number(newItemData.sellerCode) || 0,
      farmerCode: Number(newItemData.farmerCode) || 0,
      teaSeasonCode: toNumberOrNull(newItemData.teaSeasonCode),
      teaTypeCode: toNumberOrNull(newItemData.teaTypeCode),
      varietyCode: toNumberOrNull(newItemData.varietyCode),
      gapCode: toNumberOrNull(newItemData.gapCode),
      productionHistory: Number(newItemData.productionHistory) || 0,
      caltivationCategory: Number(newItemData.caltivationCategory) || 0,
    };
    return payload;
  };

  /**
   * API送信用に、リストデータの空文字列をnull/0に変換するヘルパー関数
   * BiddingInfoItem[] (配列) を受け取り、変換後の配列を返します
   */
  const prepareExcelItemListForApi = (items: ExcelInfoItem[]) => {
    // map関数を使うことで、結果は自動的に配列 [] になり、
    // 入力された件数分だけ {} (オブジェクト) が生成されます。
    return items.map((item) => {
      
      // 1. UI管理用の不要なプロパティを除外 (itemId, receptionDate)
      //    ...rest にそれ以外のプロパティが入ります
      const { itemId, receptionDate, ...rest } = item;

      // 2. API仕様に合わせて値を変換して新しいオブジェクトを返す
      return {
        ...rest, // 全てのプロパティをコピー
      };
    });
  };

  // 出品IDに紐づく出品アイテム（出品メインアイテム＋出品サブアイテム全件）を削除します。
  const deleteBiddingItem = useCallback(
    async (itemId: string): Promise<boolean> => {
      await api.delete<Item>(`/BiddingItems/${itemId}`);
      return true;
    },
    [api],
  );

  // 出品IDに紐づく出品情報ステータスを更新します。
  const updateBiddingItemStatus = useCallback(
    async (itemId: string, status: ItemStatusType): Promise<boolean> => {
      await api.put<Item>(`/BiddingItems/${itemId}/Status`, status);
      return true;
    },
    [api],
  );

  /// 複数の出品サブアイテムを一括で新規登録します。
  /// ※ 出品IDに紐づく既存の出品サブアイテムは全件削除した後にリクエスト内容で新規登録します
  /// ※ リクエスト0件の場合は出品メインアイテムをもとに出品サブアイテムを１件登録
  const upsertSubItems = useCallback(
    async (
      itemId: string,
      requests: BiddingInfoSubItem[],
    ): Promise<BiddingInfoItem> => {
      const response = await api.put<BiddingInfoItem>(
        `/BiddingItems/${itemId}/SubItems`,
        requests,
      );
      return response.data;
    },
    [api],
  );

  /// 指定された条件に一致する出品情報（＋入札情報一覧）一覧を取得します。
  /// ※ 入札情報はログインユーザーに紐づく買い手コードに属するデータのみ取得する
  const biddingInfoGetInfoListForBuyer = useCallback(
    async (queryString: string): Promise<UpcomingBiddingListResponse> => {
      const response = await api.get(`/BiddingInfo/Buyer?${queryString}`);
      return response.data;
    },
    [api],
  );

  /// 入札データを１件更新します。
  const upsertBid = useCallback(
    async (
      itemId: string,
      branchNumber: number,
      requests: UpdateBiddingDataRequest,
    ): Promise<BiddingInfoData> => {
      const response = await api.put<BiddingInfoData>(
        `/BiddingBids/${itemId}/${branchNumber}`,
        requests,
      );
      return response.data;
    },
    [api],
  );

  /**
   * 入札データの無効状態を更新します。
   */
  const updateBidDisabled = useCallback(
    async (
      itemId: string,
      branchNumber: number,
      buyerCode: number,
      isDisabled: boolean,
    ): Promise<BiddingInfoData> => {
      // Bodyには boolean をそのまま乗せて送信
      const response = await api.patch<BiddingInfoData>(
        `/BiddingBids/${itemId}/${branchNumber}/${buyerCode}/disabled`,
        isDisabled,
      );
      return response.data;
    },
    [api],
  );

  /// 指定された条件に一致する落札情報（＋入札情報一覧）一覧を取得します。
  const biddingGetAllResultList = useCallback(
    async (queryString: string): Promise<BiddingInfoResultResponse> => {
      const response = await api.get(
        `/BiddingInfo/AllResultList?${queryString}`,
      );
      return response.data;
    },
    [api],
  );

  /// 指定された条件に一致する落札情報（＋入札情報一覧）一覧を取得します(帳票用)。
  const biddingGetAllResultListForReport = useCallback(
    async (queryString: string): Promise<BiddingInfoResultResponse> => {
      const response = await api.get(
        `/BiddingInfo/Report?${queryString}`,
      );
      return response.data;
    },
    [api],
  );

  /**
   * 指定された条件に一致する落札確定一覧の情報を取得(落札確定画面用)
   */
  const biddingResultGetList = useCallback(
    async (queryString: string): Promise<BiddingInfoResultResponse> => {
      const response = await api.get(`/BiddingInfo/Confirm?${queryString}`);
      return response.data;
    }, [api]
  );

  /**
   * 出品情報のステータスを一括更新する (POST /api/BiddingItems/BulkUpdateStatus)
   * @param targetStatus 更新先のステータス
   * @param itemIds 更新対象のItemIDの配列
   * @returns 成功したかどうか (boolean)
   */
  const bulkUpdateBiddingItemStatus = useCallback(
    async (
      targetStatus: ItemStatusType,
      itemIds: string[],
    ): Promise<boolean> => {
      // 1. APIが期待するリクエストボディを作成
      const payload: BulkUpdateStatusRequest = {
        targetStatus: targetStatus,
        itemIds: itemIds,
      };

      // 3. api.post を実行
      //    (バックエンドが boolean を返すと仮定)
      const response = await api.post<boolean>(
        "/BiddingItems/BulkUpdateStatus",
        payload,
      );

      // 4. バックエンドからの成功レスポンス (true) を返す
      return response.data;
    },
    [api],
  );

  // 全権限用：出品情報一覧を取得
  const biddingInfoGetList = useCallback(
    async (queryString: string): Promise<UpcomingBiddingListResponse> => {
      const response = await api.get(`/BiddingInfo/List?${queryString}`);
      return response.data;
    },
    [api],
  );

  // 出品情報の状態(ステータス)ごとの件数を取得
  const biddingItemStatusCounts = useCallback(
    async (queryString: string): Promise<BiddingItemStatusUpdateResult> => {
      const response = await api.get(
        `/BiddingItems/StatusCounts?${queryString}`,
      );
      return response.data;
    },
    [api],
  );

  const biddingItemStatusUpdateApply = useCallback(
    async (request: BiddingItemStatusUpdateRequest) => {
      const response = await api.post(`/BiddingItems/Apply`, request);
      return response.data;
    },
    [api],
  );

  const biddingItemStatusUpdateConfirm = useCallback(
    async (request: BiddingItemStatusUpdateRequest) => {
      const response = await api.post(`/BiddingItems/Confirm`, request);
      return response.data;
    },
    [api],
  );

  /**
   * サブアイテムの落札確定承認ステータスを更新
   */
  const updateSubItemApprovalStatus = useCallback(
    async (
      itemId: string,
      branchNumber: number,
      approvalStatus: number,
    ): Promise<BiddingInfoSubItem> => {
      const response = await api.patch<BiddingInfoSubItem>(
        `/BiddingItems/${itemId}/${branchNumber}/ApprovalStatus`,
        approvalStatus,
      );
      return response.data;
    },
    [api],
  );

  /**
   * サブアイテムの落札確定承認ステータスを一括更新
   */
  const bulkUpdateSubItemApprovalStatus = useCallback(
    async (
      targets: { itemId: string; branchNumber: number }[],
      approvalStatus: number,
      pageName: string,
    ): Promise<boolean> => {
      // バックエンドの BulkApprovalStatusUpdateRequestに合わせる
      const payload = {
        targets: targets,
        approvalStatus: approvalStatus,
        updatedPage: pageName,
      };

      await api.post("/BiddingItems/BulkUpdateApprovalStatus", payload);
      return true;
    },
    [api],
  );

  /**
   * サブアイテムの最低落札値を更新
   */
  const updateSubItemBidMinPrice = useCallback(
    async (
      itemId: string,
      branchNumber: number,
      bidMinPrice: number,
    ): Promise<BiddingInfoSubItem> => {
      const response = await api.patch<BiddingInfoSubItem>(
        `/BiddingItems/${itemId}/${branchNumber}/UpdateBidMinPrice`,
        bidMinPrice,
        { headers: { "Content-Type": "application/json" } },
      );
      return response.data;
    },
    [api],
  );

  // 買手用の出品予定一覧を取得する (ListForUpcoming を叩く)
  const biddingInfoGetUpcomingList = useCallback(
    async (queryString: string): Promise<UpcomingBiddingListResponse> => {
      const response = await api.get(`/BiddingInfo/ListForUpcoming?${queryString}`);
      return response.data;
    },
    [api],
  );

  // 入札状況監視用の一覧を取得する
  const biddingGetMonitorList = useCallback(
    async (queryString: string): Promise<BiddingInfoResultResponse> => {
      const response = await api.get(
        `/BiddingInfo/Monitor?${queryString}`,
      );
      return response.data;
    },
    [api],
  );

  /**
   * お気に入りの状態を切り替えます（トグル）。
   * @param itemId 出品ID
   * @param branchNumber 枝番
   * @returns 切り替え後の状態 ({ isFavorite: boolean })
   */
  const toggleFavorite = useCallback(
    async (
      itemId: string,
      branchNumber: number,
    ): Promise<{ isFavorite: boolean }> => {
      // バックエンドの [HttpPost("toggle")] に合わせて POST で送信
      const response = await api.post<{ isFavorite: boolean }>(
        `/BiddingFavorites/toggle`,
        { itemId, branchNumber } // BuyerFavoriteRequest (record) の形式
      );
      return response.data;
    },
    [api],
  );

  const getFavorites = useCallback(async (): Promise<FavoriteKeys[]> => {
    const response = await api.get('/BiddingFavorites');
    return response.data;
  }, [api]);

  /**
   * 手動で入札No採番
   */
  const updateBiddingNumber = useCallback(
    async (
      biddingDate: number,
      listingCategory: number,
      prefectureType: number
    ): Promise<ManualNumberingResponse> => {
      const payload = {
        biddingDate,
        listingCategory,
        prefectureType,
      };
  
      const response = await api.post<ManualNumberingResponse>(
        "/BiddingItems/ManualNumbering",
        payload
      );
  
      return response.data;
    },
    [api]
  );

  /**
   * スケジュールに落札確定データを登録
   */
  const createSchedulesConfirm = useCallback(
    async (request: BiddingSchedulesRequest): Promise<BiddingSchedulesResponse> => {
      const response = await api.post<BiddingSchedulesResponse>('/BiddingSchedules/Confirm', request)
      if (!response.data.isSuccess) {
        // エラーは、useMutationのonErrorや、呼び出し元のcatchブロックに飛ぶ
        throw new Error(response.data.message || "落札確定に失敗しました。");
      }

      return response.data;
  }, [api]);  

  /**
   * 落札確定取消
   */
  const deleteSchedulesConfirm = useCallback(
    async (request: BiddingSchedulesRequest): Promise<BiddingSchedulesResponse> => {
      const response = await api.post<BiddingSchedulesResponse>('/BiddingSchedules/ConfirmCancel', request)
      if (!response.data.isSuccess) {
        // エラーは、useMutationのonErrorや、呼び出し元のcatchブロックに飛ぶ
        throw new Error(response.data.message || "落札確定取消に失敗しました。");
      }
      return response.data;
  }, [api]);

  /**
   * システムマスタのデータ取得
   */
  const getSystemMaster = useCallback(async (): Promise<SystemMaster> => {
    const response = await api.get('/masters/systemmaster')
    return response.data;
  }, [api]);

  /**
   * 入札バリデーション用の設定を取得する
   */
  const getBiddingConfig = useCallback(async (): Promise<BiddingConfig> => {
    const response = await api.get('/masters/systemmaster/bidding-config');
    return response.data;
  }, [api]);

  // 1件取得用のAPI呼び出し関数
const biddingInfoGetInfoForBuyer = useCallback(
  async (biddingDate: string | number, biddingNumber: string): Promise<BiddingInfoResponse> => {
    const response = await api.get(`/BiddingInfo/BidInfo`, {
      params: { biddingDate, biddingNumber }
    });
    return response.data;
  },
  [api],
);


  return {
    biddingItemsGetItemList,
    biddingItemsGetItem,
    createBiddingItem,
    updateBiddingItem,
    bulkCreateBiddingItem,
    deleteBiddingItem,
    upsertSubItems,
    updateBiddingItemStatus,
    biddingInfoGetInfoListForBuyer,
    upsertBid,
    updateBidDisabled,
    biddingGetAllResultList,
    biddingResultGetList,
    biddingGetAllResultListForReport,
    bulkUpdateBiddingItemStatus,
    biddingInfoGetList,
    biddingItemStatusCounts,
    biddingItemStatusUpdateApply,
    biddingItemStatusUpdateConfirm,
    updateSubItemApprovalStatus,
    bulkUpdateSubItemApprovalStatus,
    updateSubItemBidMinPrice,
    biddingInfoGetUpcomingList,
    toggleFavorite,
    getFavorites,
    createSchedulesConfirm,
    deleteSchedulesConfirm,
    getSystemMaster,
    biddingGetMonitorList,
    updateBiddingNumber,
    getBiddingConfig,
    biddingInfoGetInfoForBuyer
  };
};
